import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    useTheme,
    alpha,
    useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FavoriteIcon from "@mui/icons-material/Favorite";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import { IconButton } from "@mui/material";
import { useContext } from "react";
import { ThemeContext } from "../../index";
import FeedbackDialog from "../FeedbackDialog";

const roles = [
    {
        name: "Raja",
        emoji: "\u{1F451}",
        color: "#f59e0b",
        desc: "The King — earns the most points. Stay hidden and let Mantri protect you.",
    },
    {
        name: "Mantri",
        emoji: "\u{1F9D0}",
        color: "#3b82f6",
        desc: "The Minister — guess who the Chor is. A correct guess earns big rewards.",
    },
    {
        name: "Chor",
        emoji: "\u{1F3AD}",
        color: "#ef4444",
        desc: "The Thief — blend in and avoid being caught. Fooling Mantri is your game.",
    },
    {
        name: "Sipahi",
        emoji: "\u{1F6E1}",
        color: "#22c55e",
        desc: "The Soldier — guard the kingdom. Your score depends on Mantri's judgment.",
    },
];

const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const LandingPage = ({ onPlay }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { mode, toggleTheme } = useContext(ThemeContext);
    const [openFeedback, setOpenFeedback] = useState(false);

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                background: theme.palette.gradient.bg,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
            }}
        >
            {/* Theme Toggle (Top Right) */}
            <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 10 }}>
                <Button
                    onClick={() => setOpenFeedback(true)}
                    startIcon={<span style={{ fontSize: 16 }}>💬</span>}
                    sx={{
                        color: theme.palette.text.primary,
                        backgroundColor: alpha(theme.palette.background.paper, 0.2),
                        backdropFilter: "blur(4px)",
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        textTransform: "none",
                        fontWeight: 600,
                        px: 2,
                        mr: 1,
                        borderRadius: 2,
                        "&:hover": {
                            backgroundColor: alpha(theme.palette.background.paper, 0.4),
                            transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                    }}
                >
                    Feedback
                </Button>
                <IconButton
                    onClick={toggleTheme}
                    sx={{
                        color: theme.palette.text.primary,
                        backgroundColor: alpha(theme.palette.background.paper, 0.2),
                        backdropFilter: "blur(4px)",
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        "&:hover": {
                            backgroundColor: alpha(theme.palette.background.paper, 0.4),
                            transform: "rotate(30deg)",
                        },
                        transition: "all 0.3s ease",
                    }}
                >
                    {mode === "dark" ? <LightModeIcon /> : <DarkModeIcon />}
                </IconButton>
            </Box>
            {/* Background sparkles */}
            {[...Array(18)].map((_, i) => (
                <Box
                    key={i}
                    sx={{
                        position: "absolute",
                        width: { xs: 3, sm: 5 },
                        height: { xs: 3, sm: 5 },
                        borderRadius: "50%",
                        backgroundColor: alpha(theme.palette.primary.main, 0.25),
                        top: `${5 + Math.random() * 90}%`,
                        left: `${5 + Math.random() * 90}%`,
                        animation: `float ${2 + Math.random() * 4}s ease-in-out ${Math.random() * 3}s infinite`,
                        pointerEvents: "none",
                    }}
                />
            ))}

            {/* Hero Section */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    px: 3,
                    py: { xs: 6, md: 10 },
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    <Box
                        component="img"
                        src="/assets/logo/rmcs-logo.png"
                        alt="RMCS"
                        sx={{
                            width: { xs: 72, sm: 90 },
                            height: { xs: 72, sm: 90 },
                            borderRadius: 3,
                            mb: 2,
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                        }}
                    />
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.6 }}
                >
                    <Typography
                        variant={isMobile ? "h4" : "h2"}
                        sx={{
                            fontWeight: 900,
                            letterSpacing: { xs: 1, sm: 3 },
                            background: theme.palette.gradient.brand,
                            backgroundClip: "text",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            mb: 1.5,
                            lineHeight: 1.2,
                        }}
                    >
                        Raja Mantri Chor Sipahi
                    </Typography>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                >
                    <Typography
                        variant={isMobile ? "body2" : "h6"}
                        sx={{
                            color: theme.palette.text.secondary,
                            maxWidth: 560,
                            mx: "auto",
                            mb: 4,
                            lineHeight: 1.7,
                            fontWeight: 400,
                        }}
                    >
                        Remember the pen-and-paper game from childhood recesses?
                        Now play it online with friends, anywhere in the world.
                        Outsmart, bluff, and relive the magic!
                    </Typography>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7, type: "spring", stiffness: 200 }}
                >
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrowIcon />}
                        onClick={onPlay}
                        sx={{
                            px: 5,
                            py: 1.5,
                            fontSize: "1.1rem",
                            fontWeight: 700,
                            borderRadius: 3,
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.45)}`,
                            "&:hover": {
                                transform: "translateY(-2px) scale(1.02)",
                                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.55)}`,
                            },
                            transition: "all 0.25s ease",
                        }}
                    >
                        Play Now
                    </Button>
                </motion.div>
            </Box>

            {/* Role Cards Section */}
            <Box
                sx={{
                    px: { xs: 2, md: 6 },
                    pb: { xs: 4, md: 8 },
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.9 }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            textAlign: "center",
                            mb: 3,
                        }}
                    >
                        The Four Roles
                    </Typography>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                            gap: { xs: 1.5, md: 2.5 },
                            maxWidth: 900,
                            mx: "auto",
                        }}
                    >
                        {roles.map((role) => (
                            <motion.div key={role.name} variants={cardVariants}>
                                <Box
                                    sx={{
                                        p: { xs: 2, md: 3 },
                                        borderRadius: 3,
                                        border: `1px solid ${alpha(role.color, 0.25)}`,
                                        backgroundColor: alpha(theme.palette.background.paper, 0.6),
                                        backdropFilter: "blur(12px)",
                                        textAlign: "center",
                                        transition: "transform 0.25s ease, box-shadow 0.25s ease",
                                        "&:hover": {
                                            transform: "translateY(-4px)",
                                            boxShadow: `0 8px 24px ${alpha(role.color, 0.2)}`,
                                        },
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography sx={{ fontSize: { xs: "2rem", md: "2.5rem" }, mb: 1 }}>
                                        {role.emoji}
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 700, color: role.color, mb: 0.5 }}
                                    >
                                        {role.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            lineHeight: 1.5,
                                            fontSize: { xs: "0.65rem", md: "0.75rem" },
                                        }}
                                    >
                                        {role.desc}
                                    </Typography>
                                </Box>
                            </motion.div>
                        ))}
                    </Box>
                </motion.div>
            </Box>

            {/* How to Play */}
            <Box
                sx={{
                    px: { xs: 2, md: 6 },
                    pb: { xs: 4, md: 6 },
                    position: "relative",
                    zIndex: 1,
                }}
            >
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2 }}
                >
                    <Box
                        sx={{
                            maxWidth: 700,
                            mx: "auto",
                            p: { xs: 2.5, md: 4 },
                            borderRadius: 3,
                            border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: alpha(theme.palette.background.paper, 0.5),
                            backdropFilter: "blur(10px)",
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}>
                            How to Play
                        </Typography>
                        {[
                            "Create a room and share the code with 3 friends.",
                            "Each round, roles are randomly assigned: Raja, Mantri, Chor, Sipahi.",
                            "The Mantri must identify the Chor — a correct guess earns bonus points!",
                            "Play multiple rounds and compete for the highest score.",
                            "Chat, bluff, and outsmart your opponents!",
                        ].map((step, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    gap: 1.5,
                                    mb: 1.5,
                                    alignItems: "flex-start",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: "50%",
                                        background: theme.palette.gradient.brand,
                                        color: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "0.7rem",
                                        fontWeight: 700,
                                        flexShrink: 0,
                                        mt: 0.2,
                                    }}
                                >
                                    {i + 1}
                                </Box>
                                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                    {step}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </motion.div>
            </Box>

            {/* Footer — only on landing page */}
            <Box
                component="footer"
                sx={{
                    flexShrink: 0,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: "blur(8px)",
                    py: 1.5,
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        opacity: 0.6,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.4,
                        flexWrap: "wrap",
                        justifyContent: "center",
                    }}
                >
                    Made with
                    <FavoriteIcon sx={{ fontSize: 11, color: "#E91E63" }} />
                    for you! Developed by
                    <Box
                        component="a"
                        href="https://github.com/sushant76s"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            color: theme.palette.primary.main,
                            textDecoration: "none",
                            fontWeight: 700,
                            "&:hover": { textDecoration: "underline" },
                        }}
                    >
                        Sushant
                    </Box>
                    &nbsp;&bull;&nbsp; &copy;2026 RMCS. All rights reserved.
                </Typography>
            </Box>
            {/* Feedback / Bug Report Dialog */}
            <FeedbackDialog
                open={openFeedback}
                onClose={() => setOpenFeedback(false)}
            // roomCode={roomCode}
            />
        </Box>
    );
};

export default LandingPage;
